import { waitFor } from '../async';
import { globalFetch } from './fetch';
import {
  defaultAssertRetryableResponse,
  isRetryableError,
  isRetryableStatusCode,
  RetriedError,
  RetryableAttemptError,
  UnretryableAttemptError,
} from './retryable-errors';
import type {
  RetainedRequest,
  RetryableFetchParams,
  RetryableFetchRequest,
} from './types';

function defaultCalculateBackoff(attempt: number, _error: Error) {
  return Math.min(100 * 2 ** attempt + Math.random() * 100, 10_000);
}

// Create query fetcher with retry logic
export async function retryableFetch(
  getRequest: RetryableFetchRequest,
  {
    onAttempt,
    fetch: fetchFn = globalFetch,
    attempts = 3,
    calculateBackoff = defaultCalculateBackoff,
    onRetry,
    onFail,
    onSuccess,
    assertResponse,
    assertOnLastAttempt,
    clone,
  }: RetryableFetchParams
) {
  // Implement retry logic with exponential backoff
  let lastError: Error | null = null;
  let lastRequest: RetainedRequest | null = null;
  let lastResponse: Response | null = null;
  let attempt = 0;
  while (attempt <= attempts) {
    attempt++;
    const isLastAttempt = attempt === attempts;
    try {
      const request = await getRequest();
      lastRequest = clone?.request ? request.clone() : request;
      if (onAttempt)
        await onAttempt({ request: lastRequest, attempt, attempts });
      const response = await fetchFn(request);
      if (clone?.monkeyPatch) {
        const text = await response.text().catch(() => undefined);
        const json = (() => {
          if (!text) return undefined;
          try {
            return JSON.parse(text);
          } catch (_error) {
            return undefined;
          }
        })();
        // Response body readers are read-only properties in Workers types.
        // Keep them writable so later readers such as formatResponse can
        // replace them again.
        Object.defineProperties(response, {
          text: { value: async () => text, writable: true, configurable: true },
          json: { value: async () => json, writable: true, configurable: true },
          clone: { value: () => response, writable: true, configurable: true },
        });
      }
      lastResponse = clone?.response ? response.clone() : response;

      // Check if status code indicates we should retry
      if (!isLastAttempt || assertOnLastAttempt) {
        const retryAfter = response.headers.has('Retry-After')
          ? Number(response.headers.get('Retry-After')) * 1000
          : undefined;
        const isRetryableByStatusCode = isRetryableStatusCode(response.status);
        const assert = assertResponse || defaultAssertRetryableResponse;
        const result = await assert?.({
          attempt,
          attempts,
          isRetrying: typeof retryAfter === 'number' || isRetryableByStatusCode,
          isRetryableStatusCode: isRetryableByStatusCode,
          retryAfterMs: retryAfter,
          request,
          response,
        });
        if (result && 'errorMessage' in result) {
          if (result.retryable)
            throw new RetryableAttemptError(
              result.errorMessage!,
              result.retryAfterMs,
              result.errorDetails
            );
          throw new UnretryableAttemptError(result.errorMessage);
        }
        if (result && 'retryAfterMs' in result) {
          throw new RetryableAttemptError(
            'Retryable Error with retry-after header',
            result.retryAfterMs
          );
        }
      }

      if (onSuccess) await onSuccess({ request, response, attempt, attempts });
      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      // Only retry if this is a retryable error and we haven't exceeded max retries
      if (isLastAttempt || !isRetryableError(lastError)) break;

      // Exponential backoff: 2^retries * 100ms + some random jitter
      const backoff =
        typeof (lastError as RetryableAttemptError).retryAfterMs === 'number'
          ? (lastError as RetryableAttemptError).retryAfterMs!
          : calculateBackoff(attempt, lastError);

      // Wait before the next retry
      await waitFor(backoff);
      if (onRetry)
        await onRetry({
          error: lastError,
          attempt,
          attempts,
          request: lastRequest!,
          response: lastResponse!,
          backoffMs: backoff,
        });
    }
  }

  const error = lastError || new Error('Max retries exceeded');
  if (onFail)
    await onFail({
      error,
      attempt,
      attempts,
      request: lastRequest!,
      response: lastResponse!,
    });
  // This should never happen since we either return a result or throw an error
  throw new RetriedError(error, attempts);
}

// Create query fetcher with retry logic
export function useRetryableFetch(args: RetryableFetchParams) {
  return (request: RetryableFetchRequest) => retryableFetch(request, args);
}
